import * as React from "react";

import { DEFAULT_LOCALE, isLocale, type Locale } from "@dteba/shared";

import { dictionary, type TranslationKey } from "./dictionary";

const STORAGE_KEY = "dteba.locale";

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  /** Locale-aware date formatting for stay dates and CMS timestamps. */
  formatDate: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  formatDateRange: (from: string | Date, to: string | Date) => string;
};

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

/**
 * `?lang=id` wins, so a link to the Indonesian site can be shared and lands in
 * the right language before anything is stored. Then the previous choice, then
 * the browser's, then English.
 */
function readInitialLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(fromUrl)) return fromUrl;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  const browser = navigator.language.split("-")[0];
  return isLocale(browser) ? browser : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(readInitialLocale);

  React.useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const value = React.useMemo<LocaleContextValue>(() => {
    const intlLocale = locale === "id" ? "id-ID" : "en-GB";

    const toDate = (value: string | Date) =>
      value instanceof Date ? value : new Date(`${value}${value.length === 10 ? "T00:00:00" : ""}`);

    return {
      locale,
      setLocale: setLocaleState,
      t: (key, vars) => {
        const template = dictionary[locale][key] ?? dictionary[DEFAULT_LOCALE][key] ?? key;
        if (!vars) return template;
        return template.replace(/\{(\w+)\}/g, (match, name: string) =>
          name in vars ? String(vars[name]) : match,
        );
      },
      formatDate: (value, options) =>
        new Intl.DateTimeFormat(intlLocale, options ?? { day: "numeric", month: "short", year: "numeric" }).format(
          toDate(value),
        ),
      formatDateRange: (from, to) => {
        const start = toDate(from);
        const end = toDate(to);
        const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
        const startText = new Intl.DateTimeFormat(intlLocale, {
          day: "numeric",
          ...(sameMonth ? {} : { month: "short" }),
        }).format(start);
        const endText = new Intl.DateTimeFormat(intlLocale, {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(end);
        return `${startText}–${endText}`;
      },
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = React.useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used inside <LocaleProvider>");
  return context;
}
